// STSView — main STS screen: top button to open the recording overlay,
// compact script + video, master timeline with MasterClip chips, and the
// take-lanes stack below the divider.
//
// Upload pipeline wired via stsAssetToBlocks projection — see
// src/lib/stsToBlocks.ts. The master timeline is projected to a
// synthetic Block[] (one block per MasterClip, single fav-take each)
// so submitAssetUpload stays single-shape; the orchestrator's `isSts`
// flag routes params.txt through its STS branch.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import type { MasterClip, STSTake } from '@shared/types';
import { Button } from '@/components/ui/Button';
import { VideoPlayer } from '@/components/VideoPlayer/VideoPlayer';
import { MixerButton } from '@/components/AudioMixer/MixerButton';
import { MixerPopover } from '@/components/AudioMixer/MixerPopover';
import { ExportBar } from '@/components/Timeline/ExportBar';
import { SfxTrack } from '@/components/Timeline/SfxTrack';
import { selectAllAssets, useSessionStore } from '@/stores/session';
import { buildAssetList } from '@/lib/assetList';
import { useVideoStore } from '@/stores/video';
import { useToast } from '@/components/ToastProvider';
import { TakeLane } from '@/components/STS/TakeLane';
import { STSMasterTimeline } from '@/components/STS/STSMasterTimeline';
import { RecordingStudio } from '@/components/STS/RecordingStudio/RecordingStudio';
import { UploadModal } from '@/components/UploadModal/UploadModal';
import { getPrimaryVoiceId } from '@/lib/voiceMigration';
import { useCloudVideoSize } from '@/hooks/useCloudVideoSize';
import { stsAssetToBlocks } from '@/lib/stsToBlocks';
import { isFlexAsset } from '@/lib/assetUi';
import { OverLimitPill } from '@/components/OverLimitPill/OverLimitPill';
import { formatTakeLabel } from '@/lib/stsTakeLabel';
import { withSearch } from '@/lib/nav';
import {
  registerPlaybackStop,
  startPlayback,
  stopAllPlayback,
} from '@/stores/playback';
import { BottomBar } from './BottomBar';
import { useAssetUploadSubmit } from './hooks/useAssetUploadSubmit';
import styles from './STSView.module.css';
import { scriptAttrs } from '@/lib/scriptDetect';
import { ScriptHeader } from './components/ScriptHeader';

const scriptHeaderClasses = {
  row: styles.scriptBoxLabel,
  actions: styles.scriptBoxActions,
  button: styles.scriptCopyBtn,
  copied: styles.scriptCopyBtnCopied,
};

export function STSView() {
  const currentAssetIdx = useSessionStore((s) => s.currentAssetIdx);
  const setCurrentAssetIdx = useSessionStore((s) => s.setCurrentAssetIdx);
  const stsAssets = useSessionStore((s) => s.stsAssets);
  const addMasterClip = useSessionStore((s) => s.addMasterClip);
  const removeSTSTake = useSessionStore((s) => s.removeSTSTake);
  // Phase 6 (#173 Task 6.3): switched to upsertSTSTakeByVoice — sessions
  // commit voice-keyed (session, voiceId) slots rather than appending
  // every marked take blindly.
  const upsertSTSTakeByVoice = useSessionStore((s) => s.upsertSTSTakeByVoice);
  // Subscribe to individual slices for activeAsset — avoids rebuilding
  // on every unrelated store update (TTSView pattern).
  const configAssets = useSessionStore((s) => s.config?.assets);
  const configVoices = useSessionStore((s) => s.config?.voices);
  const customAssets = useSessionStore((s) => s.customAssets);
  // Inline selectAllAssets with vanilla's predefined free-choice voice
  // fallback (legacy line 4014). Subscribed via slices to avoid the
  // rebuilds-on-every-render trap of the live selector.
  const activeAsset = useMemo(
    // `buildAssetList` setzt `_isCustom` — ohne das Tag legte der Upload
    // `1 - Titel` statt `1 - Custom - Titel` an, während der Löschpfad den
    // Custom-Namen suchte → WebDAV-404 als Erfolg gewertet.
    // Siehe docs/decisions.md 2026-08-13.
    () => buildAssetList(configAssets, configVoices, customAssets)[currentAssetIdx],
    [configAssets, configVoices, customAssets, currentAssetIdx],
  );

  // Store slices, pulled individually so unrelated store updates don't
  // re-render. The upload-only slices live in useAssetUploadSubmit.
  const config = useSessionStore((s) => s.config);
  // Multi-Lingual (Spec 2026-09-21 § 6): `simpleMode` = „Multi-Lingual-
  // Talent" (kein SfxTrack). Video, Mixer und feste Länge entscheiden sich
  // pro Asset — nur ein Asset ohne Video bleibt flexibel.
  const simpleMode = config?._simpleMode === true;
  const flexAsset = isFlexAsset(config, activeAsset);
  const toolId = useSessionStore((s) => s.toolId);
  const sessionFolder = useSessionStore((s) => s.sessionFolder);
  const versions = useSessionStore((s) => s.versions);
  // FB#12 — free/custom assets in STS mode forward `freeMode: 'sts'` to the
  // upload (useAssetUploadSubmit) so the server writes `version.mode` and the
  // VersionsBar can render the STS chip.
  const videoBlobs = useVideoStore((s) => s.blobs);
  const totalAssets = useSessionStore((s) => selectAllAssets(s).length);

  const { id: routeId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const toast = useToast();

  // Current asset's STS state. If the slot is missing, treat it as empty —
  // we only write to the store when the user actually creates something
  // (so we don't allocate empty `stsAssets[idx]` entries eagerly).
  const stateSlot = stsAssets[currentAssetIdx];
  const state = useMemo(() => stateSlot ?? { takes: [], master: [] }, [stateSlot]);
  const takes = state.takes;
  const clips = useMemo(
    () => [...state.master].sort((a, b) => a.offset - b.offset),
    [state.master],
  );
  const takesById = useMemo(() => {
    const m: Record<string, typeof takes[number]> = {};
    takes.forEach((t) => { m[t.id] = t; });
    return m;
  }, [takes]);

  // Mixer popover open/close. Same pattern as TTSView.
  const [mixerOpen, setMixerOpen] = useState(false);
  const mixerBtnRef = useRef<HTMLDivElement | null>(null);
  const toggleMixer = useCallback((ev: React.MouseEvent) => {
    ev.stopPropagation();
    setMixerOpen((o) => !o);
  }, []);
  const closeMixer = useCallback(() => setMixerOpen(false), []);

  // Which take lane is currently playing. A single lane plays at a time —
  // starting one pauses the others via the `shouldPause` prop.
  const [playingTakeId, setPlayingTakeId] = useState<string | null>(null);

  // Map of ref counts for each take id → count of MasterClips referencing
  // it. Used in the delete-confirm dialog so the user knows the cascade.
  const refCountByTake = useMemo(() => {
    const counts: Record<string, number> = {};
    state.master.forEach((c) => {
      counts[c.takeId] = (counts[c.takeId] ?? 0) + 1;
    });
    return counts;
  }, [state.master]);

  // Copy → Master: append a full-length MasterClip at the end of the
  // existing clips' combined footprint (sum of durations for simplicity —
  // drag reflows later).
  const handleCopyToMaster = useCallback(
    (takeId: string) => {
      const take = takesById[takeId];
      if (!take) return;
      const lastEnd = clips.reduce(
        (acc, c) => Math.max(acc, c.offset + (c.outPoint - c.inPoint)),
        0,
      );
      const clip: MasterClip = {
        id: `mclip-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        takeId: take.id,
        inPoint: 0,
        outPoint: take.duration,
        offset: Math.round(lastEnd * 10) / 10,
        voiceId: take.voiceId,
      };
      addMasterClip(currentAssetIdx, clip);
      toast.show('Clip added to master timeline', 2000);
    },
    [addMasterClip, clips, currentAssetIdx, takesById, toast],
  );

  const handleRegionSelect = useCallback(
    (takeId: string, start: number, end: number) => {
      const take = takesById[takeId];
      if (!take) return;
      const lastEnd = clips.reduce(
        (acc, c) => Math.max(acc, c.offset + (c.outPoint - c.inPoint)),
        0,
      );
      const clip: MasterClip = {
        id: `mclip-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        takeId: take.id,
        inPoint: start,
        outPoint: end,
        offset: Math.round(lastEnd * 10) / 10,
        voiceId: take.voiceId,
      };
      addMasterClip(currentAssetIdx, clip);
      toast.show('Clip added to master timeline', 2000);
    },
    [addMasterClip, clips, currentAssetIdx, takesById, toast],
  );

  // Delete take → cascade MasterClips that reference it (store method
  // handles the cascade). Caller (TakeLane) already confirmed.
  const handleDeleteTake = useCallback(
    (takeId: string) => {
      if (playingTakeId === takeId) setPlayingTakeId(null);
      removeSTSTake(currentAssetIdx, takeId);
    },
    [currentAssetIdx, playingTakeId, removeSTSTake],
  );

  // Global mutex integration: lane playback claims the 'take' slot so the
  // master preview ('timeline') and lanes silence each other, and
  // stopAllPlayback() (Upload modal) reaches the lanes. `pauseAllLanes`
  // exists because setting playingTakeId to null would UN-pause everything —
  // the shouldPause prop only compares against a non-null playing id.
  const [pauseAllLanes, setPauseAllLanes] = useState(false);
  useEffect(
    () =>
      registerPlaybackStop('take', () => {
        setPauseAllLanes(true);
        setPlayingTakeId(null);
      }),
    [],
  );

  const handleLanePlay = useCallback((takeId: string) => {
    startPlayback('take');
    setPauseAllLanes(false);
    setPlayingTakeId(takeId);
  }, []);

  const [overlayOpen, setOverlayOpen] = useState(false);
  const openRecordingSession = useCallback(() => {
    // Opening the booth must silence everything — countdown beeps and the
    // recording itself would otherwise run over a playing master preview.
    stopAllPlayback();
    setOverlayOpen(true);
  }, []);
  const closeRecordingSession = useCallback(() => {
    setOverlayOpen(false);
  }, []);
  const finishRecordingSession = useCallback(
    (committed: STSTake[]) => {
      // Phase 6 (#173 Task 6.3): voice-keyed upsert. Within a session each
      // (session, voiceId) is a unique slot — replaces an existing entry
      // rather than appending a duplicate. Old sessions are untouched
      // since the session number is part of the key.
      committed.forEach((t) => upsertSTSTakeByVoice(currentAssetIdx, t));
      setOverlayOpen(false);
      if (committed.length > 0) {
        toast.show(
          committed.length === 1
            ? '1 take added to this asset'
            : `${committed.length} takes added to this asset`,
          2500,
        );
      }
    },
    [upsertSTSTakeByVoice, currentAssetIdx, toast],
  );

  const hasTakes = takes.length > 0;

  // Script reference panel — mirrors the readonly box on TTSView
  // (line 1412+). STS predefined assets ship a script from the admin panel
  // and the talent needs to see it on this surface AND in the recording
  // booth (FB#23c). Custom assets edit their script on FreeChoiceView so
  // we suppress the box here when `_isCustom`.

  // ── Upload pipeline plumbing (mirrors TTSView). STS readiness =
  //    at least one MasterClip on the timeline. The synthetic Block[]
  //    is built fresh per render via stsAssetToBlocks. ────────────────
  const [uploadOpen, setUploadOpen] = useState(false);
  const isCustom = !!activeAsset?._isCustom;
  const activeAssetVoiceId = getPrimaryVoiceId(activeAsset);
  const voiceName =
    config?.voices?.find((v) => v.id === activeAssetVoiceId)?.name ?? 'Voice';
  const stsBlocks = useMemo(
    () => stsAssetToBlocks(state),
    [state],
  );
  const allBlocksReady = stsBlocks.length > 0;
  const uploadDisabledReason = useMemo<string | null>(() => {
    if (!sessionFolder) return 'Session is still initialising';
    if (stsBlocks.length === 0) return 'Add at least one master clip';
    return null;
  }, [stsBlocks.length, sessionFolder]);
  const hasVideoForActive = !!videoBlobs[currentAssetIdx];
  // Cloud-Video des Admins — nur ohne eigenes Video des Talents ist es die
  // Quelle für das Premix-MP4 (Spec 2026-09-21 § 7). Die Größe entscheidet,
  // ob es automatisch mitgeht oder das Modal fragt.
  const cloudVideoPath =
    !hasVideoForActive && activeAsset?.videoPath ? activeAsset.videoPath : null;
  const cloudVideoSize = useCloudVideoSize(toolId, cloudVideoPath);
  const nextVersion = useMemo(() => {
    const list = versions[activeAsset?.id ?? ''] ?? [];
    return list.length > 0 ? Math.max(...list.map((v) => v.version)) + 1 : 1;
  }, [versions, activeAsset?.id]);

  // Post-upload cleanup — Option B from FB3#9 brainstorm. The asset
  // structure (title, voice, script, length) stays so the user is ready for
  // the next iteration. Master clips that defined the just-uploaded version
  // are cleared so v2 starts with an empty master timeline; the take pool is
  // intentionally preserved because the user might want to reuse recordings
  // (recording-booth model). Mirrors TTSView's takes-stripping philosophy
  // applied to the STS-canonical state shape.
  const handleUploaded = useCallback(() => {
    useSessionStore.setState((s) => {
      const cur = s.stsAssets[currentAssetIdx];
      if (!cur || cur.master.length === 0) return {};
      return {
        stsAssets: {
          ...s.stsAssets,
          [currentAssetIdx]: { ...cur, master: [] },
        },
      };
    });
  }, [currentAssetIdx]);

  // Upload-modal submit (shared with TTSView). The master timeline goes in
  // as the synthetic stsBlocks; `isSts` routes params.txt through the
  // orchestrator's STS branch.
  const handleUploadSubmit = useAssetUploadSubmit({
    activeAsset,
    currentAssetIdx,
    isCustom,
    customScriptField: 'scriptSts',
    voiceName,
    blocks: stsBlocks,
    isSts: true,
    nextVersion,
    cloudVideoSize,
    logTag: 'STSView',
    onUploaded: handleUploaded,
  });

  return (
    <div className={styles.view}>
      {activeAsset?.script && !activeAsset?._isCustom && (
        <div className={styles.scriptBox}>
          <ScriptHeader
            label={<span>Original Script</span>}
            copyText={activeAsset?.script ?? ''}
            classes={scriptHeaderClasses}
          />
          <div className={styles.scriptBoxText} {...scriptAttrs(activeAsset.script)}>{activeAsset.script}</div>
        </div>
      )}

      <div className={styles.topBar}>
        <Button onClick={openRecordingSession}>
          <i className="ph-fill ph-microphone" /> New Recording Session
        </Button>
      </div>

      {/* Flex-Asset (Multi-Lingual ohne Video, Spec 2026-09-21 § 6): keine
          Video-Referenz. Mit Video zeigt der Player es ohne Upload-Zone. */}
      {!flexAsset && <VideoPlayer />}
      {/* Bed track is now available on every asset (TTS / STS / video).
          Mounted above the master timeline so it visually pairs with the
          chip lane below — same vertical position as in TTSView.
          Multi-Lingual: SFX/Music-Bed gehört dort nicht zum Flow. */}
      {!simpleMode && <SfxTrack />}
      {/* AssetLengthControl + VersionsBar moved up to Generation/index.tsx
          (FB3#2 / FB3#7 — 2026-04-28). The .timelineHeader strip that hosted
          AssetLengthControl is gone since it had no other content. */}
      {/* Zeitlimit-Pill wie in TTSView: im Flex-Asset ist `targetDuration`
          nur ein Richtwert — Chips kommen hier aus den Master-Clips
          (getrimmte Länge = outPoint − inPoint). */}
      {flexAsset && (
        <OverLimitPill
          chips={clips.map((c) => ({ offset: c.offset, dur: Math.max(0, c.outPoint - c.inPoint) }))}
          limitSec={activeAsset?.targetDuration}
        />
      )}
      <STSMasterTimeline
        clips={clips}
        takesById={takesById}
        takeLaneOrder={takes}
        mixerSlot={
          flexAsset ? undefined : (
            <div ref={mixerBtnRef} className={styles.mixerAnchor}>
              <MixerButton isOpen={mixerOpen} onToggle={toggleMixer} />
              <MixerPopover
                isOpen={mixerOpen}
                onClose={closeMixer}
                buttonRef={mixerBtnRef}
              />
            </div>
          )
        }
      />
      <ExportBar />

      <div className={styles.divider} />

      {hasTakes && (
        <p className={styles.lanesHint}>
          <strong>STS Takes</strong> - Click+Drag a selection to add it to
          the timeline, or add the full take with the arrow-up button on
          the right
        </p>
      )}

      {hasTakes ? (
        <div className={styles.lanes}>
          {takes.map((take, i) => (
            <TakeLane
              key={take.id}
              take={take}
              label={formatTakeLabel(take, i)}
              masterClipRefCount={refCountByTake[take.id] ?? 0}
              onCopyToMaster={handleCopyToMaster}
              onDelete={handleDeleteTake}
              onPlayStart={handleLanePlay}
              shouldPause={
                pauseAllLanes ||
                (playingTakeId !== null && playingTakeId !== take.id)
              }
              onRegionSelect={handleRegionSelect}
            />
          ))}
        </div>
      ) : (
        <div className={styles.empty}>
          {/* Recording-session button is rendered once at the top (.topBar) —
              FB#28 removed the duplicate here. The hint stays so the empty
              lane area still explains what will appear once a take exists. */}
          <p className={styles.emptyHint}>
            Takes you recorded and selected will appear here. You can add them
            to the master timeline above from here and arrange / trim them
            there. You can add takes multiple times — useful when you want to
            use different parts of the same take.
          </p>
        </div>
      )}

      <RecordingStudio
        open={overlayOpen}
        onClose={closeRecordingSession}
        onFinish={finishRecordingSession}
      />

      <BottomBar
        // STS has no Play-All-Favorites (lanes play individually). The bar
        // still requires the props — keep the slot disabled and inactive.
        onPlayAll={() => { /* no-op for STS */ }}
        playAllDisabled
        playAllActive={false}
        onUpload={() => setUploadOpen(true)}
        uploadDisabled={!allBlocksReady || !sessionFolder}
        uploadDisabledReason={uploadDisabledReason}
        currentAssetIdx={currentAssetIdx}
        totalAssets={totalAssets}
        onPrev={() => {
          if (currentAssetIdx > 0) setCurrentAssetIdx(currentAssetIdx - 1);
        }}
        onNext={() => {
          if (currentAssetIdx < totalAssets - 1) {
            setCurrentAssetIdx(currentAssetIdx + 1);
          }
        }}
        onSummary={() => {
          if (routeId) navigate(withSearch(`/${routeId}/summary`, location.search));
        }}
      />

      {activeAsset && (
        <UploadModal
          open={uploadOpen}
          nextVersion={nextVersion}
          readyCount={stsBlocks.length}
          hasVideo={hasVideoForActive}
          cloudVideoSize={cloudVideoSize}
          onClose={() => setUploadOpen(false)}
          onSubmit={handleUploadSubmit}
        />
      )}
    </div>
  );
}
